import { useEffect, useState, useCallback, useRef } from "react";
import { get, put, post } from "../../services/smartApiService";
import { MIRROR_URLS, KITE2_URLS, UPSTOX_URLS, MIRROR_ORDER_URLS, GUARD_URLS, PNL_SNAPSHOT_URLS } from "../../api/MirrorUrls";
import { KITE_URLS } from "../../api/KiteUrls";
import { useSiteContext } from "../../contexts/SiteProvider";

// ─── Types ───────────────────────────────────────────────────────────────────

type Broker = "kite2" | "upstox";

interface Position {
  tradingsymbol: string;
  exchange: string;
  product: string;
  quantity: number;
  average_price: number;
  last_price: number;
  pnl: number;
  unrealised: number;
  realised: number;
}

interface Order {
  order_id: string;
  tradingsymbol: string;
  exchange: string;
  transaction_type: string;
  order_type: string;
  product: string;
  quantity: number;
  price: number;
  average_price: number;
  filled_quantity: number;
  status: string;
  order_timestamp: string;
}

interface MirrorEntry {
  source_order_id: string;
  dest_broker: string;
  dest_order_id: string | null;
  tradingsymbol: string;
  transaction_type: string;
  source_qty: number;
  mirror_pct_applied: number;
  lot_size_used: number | null;
  dest_qty: number | null;
  source_status: string;
  dest_status: string | null;
  mirror_status: string;
  mirror_error: string | null;
  created_at: string | null;
}

interface Guard {
  id: number;
  broker: string;
  order_id: string;
  tradingsymbol: string;
  exchange: string;
  trigger_price: number;
  direction: "BELOW" | "ABOVE";
  time_buffer_sec: number;
  trail_offset: number;
  status: "WATCHING" | "ARMED" | "TRIGGERED" | "CANCELLED" | "EXPIRED";
  armed_at: string | null;
  armed_secs: number | null;
  triggered_at: string | null;
  last_ltp: number | null;
  created_at: string | null;
}

interface CloseAction {
  position: Position;
  closeDir: "SELL" | "BUY";
  closeQty: number;
  toConvert: Order | null;
  toCancel: string[];
}

type DrawerMode =
  | { type: "mkt-close";    position: Position }
  | { type: "limit-order";  position: Position }
  | { type: "modify-order"; order: Order }
  | { type: "new-order" }
  | { type: "create-guard" }
  | null;

interface Toast { id: number; msg: string; ok: boolean }

interface PnlSnapshot {
  id: number;
  trade_date: string;
  broker: string;
  pnl: number;
  realised: number;
  unrealised: number;
  saved_by: string;
  note: string | null;
  saved_at: string;
}

interface DestinationBlock {
  orders: Order[];
  positions: Position[];
  mirror_map: MirrorEntry[];
  summary: {
    total_mirrored: number;
    failed: number;
    skipped: number;
    cancelled_on_dest: number;
    pnl: number;
  };
}

interface AccountConn {
  is_connected: boolean;
  user_id?: string;
}

interface BrokerConfig {
  is_enabled: boolean;
  mirror_pct: number;
  updated_at: string | null;
}

interface MirrorConfigResponse {
  kite2: BrokerConfig;
  upstox: BrokerConfig;
  lot_sizes: { symbol_prefix: string; exchange: string; lot_size: number }[];
}

const BROKER_META: Record<Broker, { label: string; accentColor: string; loginUrl: string }> = {
  kite2:  { label: "Kite2",  accentColor: "#26a65b", loginUrl: KITE2_URLS.LOGIN },
  upstox: { label: "Upstox", accentColor: "#f39c12", loginUrl: UPSTOX_URLS.LOGIN },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);

const fmtTime = (ts: string) => {
  if (!ts) return "—";
  try { return new Date(ts).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
  catch { return ts; }
};

const pnlColor = (v: number) => (v >= 0 ? "#26a65b" : "#e74c3c");

const ORDER_STATUS_COLOR: Record<string, string> = {
  COMPLETE: "#26a65b", OPEN: "#4fc3f7", CANCELLED: "#888",
  REJECTED: "#e74c3c", TRIGGER_PENDING: "#f39c12",
};

const MIRROR_STATUS_COLOR: Record<string, string> = {
  MIRRORED:          "#26a65b",
  FAILED:            "#e74c3c",
  SKIPPED:           "#666",
  DISABLED:          "#f39c12",
  CANCELLED_ON_DEST: "#9b59b6",
};

const Badge = ({ color, text }: { color: string; text: string }) => (
  <span style={{
    background: `${color}22`, color, border: `1px solid ${color}44`,
    borderRadius: 4, padding: "2px 7px", fontSize: 11, fontWeight: 600,
  }}>{text}</span>
);

// ─── Toast ───────────────────────────────────────────────────────────────────

const ToastContainer = ({ toasts, onDismiss }: { toasts: Toast[]; onDismiss: (id: number) => void }) => (
  <div style={{ position: "fixed", bottom: 24, right: 24, zIndex: 3000, display: "flex", flexDirection: "column-reverse", gap: 8, pointerEvents: "none" }}>
    <style>{`
      @keyframes toastIn  { from { opacity:0; transform:translateY(12px); } to { opacity:1; transform:translateY(0); } }
      @keyframes drawerIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
    `}</style>
    {toasts.map(t => (
      <div key={t.id} style={{
        pointerEvents: "all",
        background: t.ok ? "#0a1f10" : "#1f0a0a",
        border: `1px solid ${t.ok ? "#26a65b" : "#e74c3c"}`,
        borderRadius: 8, padding: "10px 14px",
        display: "flex", alignItems: "center", gap: 10,
        color: t.ok ? "#26a65b" : "#e74c3c", fontSize: 13, fontWeight: 600,
        boxShadow: "0 4px 24px rgba(0,0,0,0.6)", minWidth: 200, maxWidth: 340,
        animation: "toastIn 0.18s ease",
      }}>
        <span style={{ flex: 1 }}>{t.ok ? "✓" : "✕"} {t.msg}</span>
        <button onClick={() => onDismiss(t.id)} style={{
          background: "transparent", border: "none", color: "inherit",
          cursor: "pointer", fontSize: 15, opacity: 0.5, padding: 0,
        }}>✕</button>
      </div>
    ))}
  </div>
);

// ─── Stat Card ───────────────────────────────────────────────────────────────

const StatCard = ({ label, value, sub, color, pulse }: {
  label: string; value: string; sub?: string; color?: string; pulse?: "up" | "down" | null;
}) => (
  <div style={{
    background: "#0c0c18",
    border: `1px solid ${pulse === "up" ? "#26a65b" : pulse === "down" ? "#e74c3c" : "#1a1a2e"}`,
    borderRadius: 8, padding: "11px 14px", flex: 1, minWidth: 90,
    boxShadow: pulse === "up" ? "0 0 10px #26a65b33" : pulse === "down" ? "0 0 10px #e74c3c33" : "none",
    transition: "border-color 0.4s, box-shadow 0.4s",
  }}>
    <div style={{ fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 1, marginBottom: 4 }}>{label}</div>
    <div style={{ fontSize: 17, fontWeight: 700, color: color || "#e8e8f0" }}>{value}</div>
    {sub && <div style={{ fontSize: 10, color: "#444", marginTop: 2 }}>{sub}</div>}
  </div>
);

// ─── New Order Form ───────────────────────────────────────────────────────────

const inputStyle = (width: number): React.CSSProperties => ({
  width, background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5,
  color: "#e8e8f0", padding: "5px 6px", fontSize: 12,
});

const selStyle = (width: number, color?: string): React.CSSProperties => ({
  ...inputStyle(width), cursor: "pointer", color: color || "#e8e8f0",
});

const UNDERLYING_EXCHANGE: Record<string, "NFO" | "BFO"> = { NIFTY: "NFO", SENSEX: "BFO" };
const LOT_SIZE: Record<string, number>                    = { NIFTY: 65, SENSEX: 20 };

type ChainData = { expiries: string[]; by_expiry: Record<string, Record<string, string>> };

// ─── Searchable Strike Picker ─────────────────────────────────────────────────
const StrikePicker = ({
  strikes, value, onChange, disabled,
}: {
  strikes: string[];
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) => {
  const [query, setQuery]   = useState("");
  const [open, setOpen]     = useState(false);
  const wrapRef             = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = strikes.filter(s => s.includes(query));

  const select = (s: string) => { onChange(s); setQuery(""); setOpen(false); };

  return (
    <div ref={wrapRef} style={{ position: "relative", display: "inline-block" }}>
      <input
        value={open ? query : (value || "")}
        placeholder={disabled ? "—" : "Strike"}
        disabled={disabled}
        onChange={e => { setQuery(e.target.value); onChange(""); setOpen(true); }}
        onFocus={() => { setQuery(""); setOpen(true); }}
        style={{
          ...inputStyle(86),
          cursor: disabled ? "not-allowed" : "text",
          color: value && !open ? "#f39c12" : "#e8e8f0",
          fontWeight: value && !open ? 700 : 400,
        }}
      />
      {open && filtered.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, zIndex: 999,
          background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5,
          maxHeight: 220, overflowY: "auto", minWidth: 100,
          boxShadow: "0 4px 16px #00000088",
        }}>
          {filtered.map(s => (
            <div
              key={s}
              onMouseDown={() => select(s)}
              style={{
                padding: "6px 10px", fontSize: 12, cursor: "pointer",
                color: s === value ? "#f39c12" : "#e8e8f0",
                fontWeight: s === value ? 700 : 400,
                background: s === value ? "#f39c1211" : "transparent",
              }}
              onMouseEnter={e => (e.currentTarget.style.background = "#2a2a3e")}
              onMouseLeave={e => (e.currentTarget.style.background = s === value ? "#f39c1211" : "transparent")}
            >
              {s}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const NewOrderForm = ({
  broker, accentColor, onSubmitted, onMsg,
}: {
  broker: Broker;
  accentColor: string;
  onSubmitted: () => void;
  onMsg?: (msg: string, ok?: boolean) => void;
}) => {
  const [underlying, setUnderlying] = useState<"NIFTY" | "SENSEX">("NIFTY");
  const [expiry, setExpiry]         = useState("");
  const [strike, setStrike]         = useState("");
  const [optType, setOptType]       = useState<"CE" | "PE">("CE");
  const [txn, setTxn]               = useState<"BUY" | "SELL">("BUY");
  const [lots, setLots]             = useState("1");
  const [product, setProduct]       = useState<"MIS" | "NRML">("MIS");
  const [orderType, setOrderType]   = useState<"MARKET" | "LIMIT" | "SL" | "SL-M">("MARKET");
  const [price, setPrice]           = useState("");
  const [triggerPrice, setTriggerPrice] = useState("");
  const [chain, setChain]           = useState<ChainData | null>(null);
  const [loadingChain, setLoadingChain] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const lotSize  = LOT_SIZE[underlying] ?? 1;
  const totalQty = (parseInt(lots, 10) || 0) * lotSize;

  const exchange = UNDERLYING_EXCHANGE[underlying];

  // Sorted unique strikes for the selected expiry
  const strikes = chain && expiry
    ? [...new Set(
        Object.keys(chain.by_expiry[expiry] || {}).map(k => k.split("_")[0])
      )].sort((a, b) => Number(a) - Number(b))
    : [];

  // Resolved Kite tradingsymbol
  const resolvedSymbol = chain && expiry && strike
    ? (chain.by_expiry[expiry]?.[`${strike}_${optType}`] || "")
    : "";

  // Fetch instrument chain whenever underlying changes
  useEffect(() => {
    setLoadingChain(true);
    setExpiry(""); setStrike(""); setChain(null);
    get(`${KITE_URLS.OPTION_CHAIN}?underlying=${underlying}&exchange=${exchange}`,
      { showLoading: false }).subscribe({
      next: (r: any) => {
        const data: ChainData = r.data;
        setChain(data);
        if (data.expiries.length > 0) setExpiry(data.expiries[0]);
        setLoadingChain(false);
      },
      error: () => setLoadingChain(false),
    });
  }, [underlying]);

  // Reset strike when expiry changes
  useEffect(() => { setStrike(""); }, [expiry]);

  const submit = () => {
    if (!resolvedSymbol) { setError("Select expiry, strike and CE/PE to resolve the contract"); return; }
    const l = parseInt(lots, 10);
    if (!l || l <= 0) { setError("Lots must be positive"); return; }
    setError(null);
    setSubmitting(true);
    post(MIRROR_ORDER_URLS.create(broker), {
      tradingsymbol: resolvedSymbol,
      exchange,
      transaction_type: txn,
      quantity: l * lotSize,
      product,
      order_type: orderType,
      price:         (orderType === "LIMIT" || orderType === "SL")    ? parseFloat(price || "0") : 0,
      trigger_price: (orderType === "SL"    || orderType === "SL-M")  ? parseFloat(triggerPrice || "0") : 0,
    }, { showLoading: false, handleError: (msg: string) => { onMsg?.(msg, false); setSubmitting(false); } }).subscribe({
      next: () => {
        onMsg?.(`${txn} ${resolvedSymbol} — ${l} lot${l > 1 ? "s" : ""} (${l * lotSize} qty) placed`, true);
        setStrike(""); setPrice(""); setTriggerPrice("");
        onSubmitted();
      },
      complete: () => setSubmitting(false),
    });
  };

  return (
    <div style={{ padding: "4px 2px" }}>
      {/* Row 1: contract selection */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
        {/* Underlying */}
        <select value={underlying} onChange={e => setUnderlying(e.target.value as any)} style={selStyle(88)}>
          <option value="NIFTY">NIFTY</option>
          <option value="SENSEX">SENSEX</option>
        </select>

        {/* Expiry */}
        <select value={expiry} onChange={e => setExpiry(e.target.value)}
          disabled={loadingChain || !chain}
          style={selStyle(110, loadingChain ? "#555" : "#e8e8f0")}>
          {loadingChain
            ? <option>Loading…</option>
            : (chain?.expiries || []).map(ex => <option key={ex} value={ex}>{ex}</option>)
          }
        </select>

        {/* Strike — searchable */}
        <StrikePicker
          strikes={strikes}
          value={strike}
          onChange={setStrike}
          disabled={!expiry || strikes.length === 0}
        />

        {/* CE / PE */}
        <select value={optType} onChange={e => setOptType(e.target.value as any)}
          style={selStyle(60, optType === "CE" ? "#4fc3f7" : "#f39c12")}>
          <option value="CE">CE</option>
          <option value="PE">PE</option>
        </select>

        {/* Resolved symbol preview */}
        <span style={{
          fontSize: 12, fontWeight: 700, letterSpacing: 0.5,
          color: resolvedSymbol ? accentColor : "#333",
          minWidth: 160, fontFamily: "monospace",
        }}>
          {resolvedSymbol || (loadingChain ? "loading…" : "— select contract —")}
        </span>
      </div>

      {/* Row 2: order params */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        <select value={txn} onChange={e => setTxn(e.target.value as any)}
          style={selStyle(66, txn === "BUY" ? "#26a65b" : "#e74c3c")}>
          <option value="BUY">BUY</option>
          <option value="SELL">SELL</option>
        </select>

        <input placeholder="Lots" type="number" min={1} value={lots}
          onChange={e => setLots(e.target.value)} style={inputStyle(48)} />
        <span style={{ fontSize: 11, color: "#555", whiteSpace: "nowrap" }}>
          ×{lotSize} = <span style={{ color: "#e8e8f0", fontWeight: 700 }}>{totalQty}</span>
        </span>

        <select value={product} onChange={e => setProduct(e.target.value as any)} style={selStyle(64)}>
          <option value="MIS">MIS</option>
          <option value="NRML">NRML</option>
        </select>

        <select value={orderType} onChange={e => setOrderType(e.target.value as any)} style={selStyle(80)}>
          <option value="MARKET">MARKET</option>
          <option value="LIMIT">LIMIT</option>
          <option value="SL">SL</option>
          <option value="SL-M">SL-M</option>
        </select>

        {(orderType === "LIMIT" || orderType === "SL") && (
          <input placeholder="Price" type="number" value={price}
            onChange={e => setPrice(e.target.value)} style={inputStyle(72)} />
        )}
        {(orderType === "SL" || orderType === "SL-M") && (
          <input placeholder="Trigger" type="number" value={triggerPrice}
            onChange={e => setTriggerPrice(e.target.value)} style={inputStyle(72)} />
        )}

        <button onClick={submit} disabled={submitting || !resolvedSymbol} style={{
          background: `${accentColor}22`, border: `1px solid ${accentColor}66`, borderRadius: 5,
          color: accentColor, padding: "5px 14px", fontSize: 12, fontWeight: 700,
          cursor: submitting || !resolvedSymbol ? "not-allowed" : "pointer",
          opacity: submitting || !resolvedSymbol ? 0.4 : 1,
        }}>{submitting ? "Placing…" : "Place order"}</button>

        {error && <span style={{ color: "#e74c3c", fontSize: 11 }}>{error}</span>}
      </div>
    </div>
  );
};

// ─── Account Panel ───────────────────────────────────────────────────────────

type PanelTab = "positions" | "orders" | "guards";

const AccountPanel = ({
  title, accentColor, conn, loginUrl, positions, orders, onRefresh, broker, onActionDone, onMsg,
}: {
  title: string;
  accentColor: string;
  conn: AccountConn | null;
  loginUrl: string;
  positions: Position[];
  orders: Order[];
  onRefresh: () => void;
  broker?: Broker;
  onActionDone?: () => void;
  onMsg?: (msg: string, ok?: boolean) => void;
}) => {
  const [tab, setTab]           = useState<PanelTab>("positions");
  const [maximized, setMaximized]         = useState(false);
  const [drawer, setDrawer]               = useState<DrawerMode>(null);
  const [closingKey, setClosingKey]         = useState<string | null>(null);
  const [closeQty, setCloseQty]             = useState("");
  const [limitSellQty, setLimitSellQty]     = useState("");
  const [limitSellPrice, setLimitSellPrice] = useState("");
  const [submittingLimit, setSubmittingLimit] = useState(false);
  const [editFields, setEditFields]       = useState<{ qty: string; price: string; trigger: string; orderType: string }>({ qty: "", price: "", trigger: "", orderType: "LIMIT" });
  const [modifying, setModifying]         = useState(false);
  const [cancellingAll, setCancellingAll]       = useState(false);
  const [executingAll, setExecutingAll]         = useState(false);
  const [confirmCancelId, setConfirmCancelId]   = useState<string | null>(null);
  const [selectedOrders, setSelectedOrders]     = useState<Set<string>>(new Set());
  const [bulkPrice, setBulkPrice]               = useState("");
  const [applyingBulk, setApplyingBulk]         = useState(false);
  const [searchQuery, setSearchQuery]           = useState("");
  const prevPnlRef  = useRef<number>(0);
  const [pnlPulse, setPnlPulse] = useState<"up" | "down" | null>(null);
  // Guards
  const [guards, setGuards]             = useState<Guard[]>([]);
  const [guardTrigger, setGuardTrigger] = useState("");
  const [guardBuffer, setGuardBuffer]   = useState("0");
  const [guardTrail, setGuardTrail]     = useState("0");
  const [guardDirection, setGuardDirection] = useState<"BELOW" | "ABOVE">("BELOW");
  const [guardOrders, setGuardOrders]   = useState<Set<string>>(new Set());
  const [creatingGuard, setCreatingGuard] = useState(false);
  // Close All
  const [closeAllPlan, setCloseAllPlan] = useState<CloseAction[] | null>(null);
  const [closingAll, setClosingAll]     = useState(false);

  const connected      = conn?.is_connected;
  const totalPnl       = positions.reduce((s, p) => s + (p.pnl || 0), 0);

  const q = searchQuery.trim().toLowerCase();
  const filteredPos    = q ? positions.filter(p => p.tradingsymbol.toLowerCase().includes(q)) : positions;
  const filteredOrders = q ? orders.filter(o => o.tradingsymbol.toLowerCase().includes(q)) : orders;

  const openPos        = filteredPos.filter(p => p.quantity !== 0);
  const closedPos      = filteredPos.filter(p => p.quantity === 0);
  const activeOrders   = filteredOrders.filter(o => o.status === "OPEN" || o.status === "TRIGGER_PENDING");
  const executedOrders = filteredOrders.filter(o => o.status === "COMPLETE");
  const closedOrders   = filteredOrders.filter(o => o.status !== "OPEN" && o.status !== "TRIGGER_PENDING" && o.status !== "COMPLETE");

  // P&L pulse when value changes
  useEffect(() => {
    if (prevPnlRef.current === 0 && totalPnl === 0) return;
    if (prevPnlRef.current !== totalPnl) {
      setPnlPulse(totalPnl > prevPnlRef.current ? "up" : "down");
      prevPnlRef.current = totalPnl;
      const t = setTimeout(() => setPnlPulse(null), 900);
      return () => clearTimeout(t);
    }
  }, [totalPnl]);

  // Esc closes maximized
  useEffect(() => {
    if (!maximized) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setMaximized(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [maximized]);

  // Esc closes drawer
  useEffect(() => {
    if (!drawer) return;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setDrawer(null); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [drawer]);

  // Fetch guards on mount and whenever broker changes; also re-fetched on guards tab open
  const fetchGuards = useCallback(() => {
    if (!broker) return;
    get(GUARD_URLS.list(broker), { showLoading: false }).subscribe({
      next: (r: any) => setGuards(r.data?.guards || []),
      error: () => {},
    });
  }, [broker]);

  useEffect(() => {
    fetchGuards();
  }, [fetchGuards]);

  useEffect(() => {
    if (tab === "guards") fetchGuards();
  }, [tab, fetchGuards]);

  const createGuards = () => {
    if (!broker || guardOrders.size === 0 || !guardTrigger) return;
    const triggerPrice = parseFloat(guardTrigger);
    if (!triggerPrice || triggerPrice <= 0) return;
    setCreatingGuard(true);
    // Find tradingsymbol/exchange from one of the selected orders
    const refOrder = activeOrders.find(o => guardOrders.has(o.order_id));
    post(GUARD_URLS.create(broker), {
      order_ids: Array.from(guardOrders),
      tradingsymbol: refOrder?.tradingsymbol || "",
      exchange: refOrder?.exchange || "NFO",
      trigger_price: triggerPrice,
      direction: guardDirection,
      time_buffer_sec: parseInt(guardBuffer, 10) || 0,
      trail_offset: parseFloat(guardTrail) || 0,
    }, { showLoading: false, handleError: (msg: string) => { onMsg?.(msg, false); setCreatingGuard(false); } }).subscribe({
      next: (r: any) => {
        const n = r.data?.created?.length ?? 0;
        const sk = r.data?.skipped?.length ?? 0;
        onMsg?.(`${n} guard(s) created${sk ? `, ${sk} already exist` : ""}`, true);
        setGuardOrders(new Set());
        setGuardTrigger("");
        setGuardTrail("0");
        fetchGuards();
      },
      complete: () => setCreatingGuard(false),
    });
  };

  const cancelGuard = (guardId: number) => {
    if (!broker) return;
    // Optimistic update — flip to CANCELLED immediately so UI responds instantly
    setGuards(gs => gs.map(g => g.id === guardId ? { ...g, status: "CANCELLED" as const } : g));
    import("../../config/config").then(({ default: cfg }) => {
      const token = localStorage.getItem("access_token");
      fetch(`${(cfg.API_URL || "").replace(/\/$/, "")}${GUARD_URLS.cancel(broker, guardId)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      }).then(r => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      }).then(() => {
        onMsg?.("Guard cancelled", true);
        fetchGuards(); // confirm from server
      }).catch(err => {
        fetchGuards(); // revert optimistic update with real state
        onMsg?.(`Cancel failed: ${err.message}`, false);
      });
    });
  };

  const buildCloseAllPlan = () => {
    const plan: CloseAction[] = openPos.map(p => {
      const closeDir = p.quantity > 0 ? "SELL" as const : "BUY" as const;
      const closeQty = Math.abs(p.quantity);
      const conflicts = activeOrders.filter(
        o => o.tradingsymbol === p.tradingsymbol && o.transaction_type === closeDir
      );
      if (conflicts.length === 0) {
        return { position: p, closeDir, closeQty, toConvert: null, toCancel: [] };
      }
      const sorted = [...conflicts].sort(
        (a, b) => Math.abs(a.quantity - closeQty) - Math.abs(b.quantity - closeQty)
      );
      return { position: p, closeDir, closeQty, toConvert: sorted[0], toCancel: sorted.slice(1).map(o => o.order_id) };
    });
    setCloseAllPlan(plan);
  };

  const executeCloseAll = () => {
    if (!broker || !closeAllPlan || closingAll) return;
    setClosingAll(true);
    let done = 0, failed = 0;
    const total = closeAllPlan.length;
    const finish = () => {
      if (done + failed < total) return;
      onMsg?.(`${done} position(s) closed${failed ? `, ${failed} failed` : ""}`, failed === 0);
      setClosingAll(false);
      setCloseAllPlan(null);
      onActionDone?.();
    };

    for (const action of closeAllPlan) {
      const doClose = () => {
        if (action.toConvert) {
          put(MIRROR_ORDER_URLS.modify(broker, action.toConvert.order_id),
            { quantity: action.closeQty, order_type: "MARKET" },
            { showLoading: false, handleError: () => { failed++; finish(); } }
          ).subscribe({ next: () => { done++; finish(); } });
        } else {
          const signedQty = action.position.quantity > 0 ? action.closeQty : -action.closeQty;
          post(MIRROR_ORDER_URLS.closePosition(broker), {
            tradingsymbol: action.position.tradingsymbol,
            exchange: action.position.exchange,
            product: action.position.product,
            quantity: signedQty,
          }, { showLoading: false, handleError: () => { failed++; finish(); } })
          .subscribe({ next: () => { done++; finish(); } });
        }
      };

      if (action.toCancel.length > 0) {
        let settled = 0;
        const onCancelSettled = () => { settled++; if (settled === action.toCancel.length) doClose(); };
        action.toCancel.forEach(orderId =>
          post(MIRROR_ORDER_URLS.cancelOrder(broker, orderId), {}, { showLoading: false })
          .subscribe({ complete: onCancelSettled })
        );
      } else {
        doClose();
      }
    }
  };

  const closePosition = (p: Position) => {
    if (!broker) return;
    const key = `${p.tradingsymbol}:${p.product}`;
    const requestedQty = parseInt(closeQty, 10) || Math.abs(p.quantity);
    const signedQty = p.quantity > 0 ? requestedQty : -requestedQty;
    setClosingKey(key);
    setCloseQty("");
    post(MIRROR_ORDER_URLS.closePosition(broker), {
      tradingsymbol: p.tradingsymbol,
      exchange: p.exchange,
      product: p.product,
      quantity: signedQty,
    }, { showLoading: false, handleError: (msg: string) => { onMsg?.(msg, false); setClosingKey(null); } }).subscribe({
      next: () => { onMsg?.(`Close order sent for ${p.tradingsymbol} (qty ${requestedQty})`, true); setDrawer(null); onActionDone?.(); },
      complete: () => setClosingKey(null),
    });
  };

  const openLimitSell = (p: Position) => {
    setLimitSellQty(String(Math.abs(p.quantity)));
    setLimitSellPrice(p.last_price > 0 ? String(p.last_price) : "");
    setDrawer({ type: "limit-order", position: p });
  };

  const submitLimitSell = () => {
    if (!broker || drawer?.type !== "limit-order") return;
    const p = drawer.position;
    const qty = parseInt(limitSellQty, 10);
    const price = parseFloat(limitSellPrice);
    if (!qty || qty <= 0 || !price || price <= 0) return;
    setSubmittingLimit(true);
    post(MIRROR_ORDER_URLS.create(broker), {
      tradingsymbol: p.tradingsymbol,
      exchange: p.exchange,
      transaction_type: p.quantity > 0 ? "SELL" : "BUY",
      order_type: "LIMIT",
      quantity: qty,
      product: p.product,
      price,
      trigger_price: 0,
    }, { showLoading: false, handleError: (msg: string) => { onMsg?.(msg, false); setSubmittingLimit(false); } }).subscribe({
      next: () => {
        onMsg?.(`LIMIT ${p.quantity > 0 ? "SELL" : "BUY"} @ ₹${price} placed for ${p.tradingsymbol}`, true);
        setDrawer(null);
        setLimitSellQty("");
        setLimitSellPrice("");
        onActionDone?.();
      },
      complete: () => setSubmittingLimit(false),
    });
  };

  const startEdit = (o: Order) => {
    setEditFields({
      qty: String(o.quantity),
      price: o.price > 0 ? String(o.price) : "",
      trigger: o.order_type === "SL" || o.order_type === "SL-M" ? String(o.price || "") : "",
      orderType: o.order_type,
    });
    setDrawer({ type: "modify-order", order: o });
  };

  const submitModify = () => {
    if (!broker || drawer?.type !== "modify-order") return;
    const orderId = drawer.order.order_id;
    const qty = parseInt(editFields.qty, 10);
    if (isNaN(qty) || qty <= 0) return;
    setModifying(true);
    const body: Record<string, any> = { quantity: qty, order_type: editFields.orderType };
    if (editFields.orderType === "LIMIT" || editFields.orderType === "SL")
      body.price = parseFloat(editFields.price || "0");
    if (editFields.orderType === "SL" || editFields.orderType === "SL-M")
      body.trigger_price = parseFloat(editFields.trigger || "0");
    put(MIRROR_ORDER_URLS.modify(broker, orderId), body, { showLoading: false, handleError: (msg: string) => { onMsg?.(msg, false); setModifying(false); } }).subscribe({
      next: () => { onMsg?.(`Order modified`, true); setDrawer(null); onActionDone?.(); },
      complete: () => setModifying(false),
    });
  };

  const cancelOrder = (orderId: string, symbol: string) => {
    if (!broker) return;
    post(MIRROR_ORDER_URLS.cancelOrder(broker, orderId), {}, { showLoading: false, handleError: (msg: string) => onMsg?.(msg, false) }).subscribe({
      next: () => { onMsg?.(`${symbol} cancelled`, true); onActionDone?.(); },
    });
  };

  const cancelAllOrders = () => {
    if (!broker || cancellingAll) return;
    setCancellingAll(true);
    post(MIRROR_ORDER_URLS.cancelAll(broker), {}, { showLoading: false, handleError: (msg: string) => { onMsg?.(msg, false); setCancellingAll(false); } }).subscribe({
      next: (r: any) => {
        const n = r.data?.cancelled?.length ?? 0;
        const errs = r.data?.errors?.length ?? 0;
        onMsg?.(`Cancelled ${n} order(s)${errs ? `, ${errs} failed` : ""}`, errs === 0);
        onActionDone?.();
      },
      complete: () => setCancellingAll(false),
    });
  };

  // Phase 1: cancel all open orders for position symbols → Phase 2: MARKET close each position
  const executeAllAsMarket = () => {
    if (!broker || executingAll || openPos.length === 0) return;
    setExecutingAll(true);

    const posSymbols = new Set(openPos.map(p => p.tradingsymbol));
    const toCancel   = activeOrders.filter(o => posSymbols.has(o.tradingsymbol));

    const phase2 = () => {
      let done = 0, failed = 0;
      const finish = () => {
        if (done + failed < openPos.length) return;
        onMsg?.(`Closed ${done} position(s)${failed ? `, ${failed} failed` : ""}`, failed === 0);
        setExecutingAll(false);
        onActionDone?.();
      };
      openPos.forEach(p => {
        post(
          MIRROR_ORDER_URLS.closePosition(broker),
          { tradingsymbol: p.tradingsymbol, exchange: p.exchange, product: p.product, quantity: p.quantity },
          { showLoading: false, handleError: () => { failed++; finish(); } }
        ).subscribe({ next: () => { done++; finish(); } });
      });
    };

    if (toCancel.length === 0) {
      phase2();
      return;
    }

    // Cancel all conflicting orders; proceed to phase2 once every request settles
    let settled = 0;
    toCancel.forEach(o => {
      post(MIRROR_ORDER_URLS.cancelOrder(broker, o.order_id), {}, { showLoading: false })
        .subscribe({ complete: () => { if (++settled >= toCancel.length) phase2(); } });
    });
  };

  // Bulk-modify selected orders
  const bulkModifySelected = (mode: "market" | "price") => {
    if (!broker || applyingBulk || selectedOrders.size === 0) return;
    const targets = activeOrders.filter(o => selectedOrders.has(o.order_id));
    if (targets.length === 0) return;
    if (mode === "price" && !bulkPrice) return;
    setApplyingBulk(true);
    let done = 0; let failed = 0;
    const finish = () => {
      if (done + failed < targets.length) return;
      const verb = mode === "market" ? "converted to MARKET" : `price set to ${bulkPrice}`;
      onMsg?.(`${done} order(s) ${verb}${failed ? `, ${failed} failed` : ""}`, failed === 0);
      setApplyingBulk(false);
      setSelectedOrders(new Set());
      setBulkPrice("");
      onActionDone?.();
    };
    targets.forEach(o => {
      const body = mode === "market"
        ? { quantity: o.quantity, order_type: "MARKET", price: 0, trigger_price: 0 }
        : { quantity: o.quantity, order_type: o.order_type === "MARKET" ? "LIMIT" : o.order_type, price: parseFloat(bulkPrice) };
      put(MIRROR_ORDER_URLS.modify(broker, o.order_id), body, { showLoading: false, handleError: () => { failed++; finish(); } }).subscribe({
        next: () => { done++; finish(); },
      });
    });
  };

  const toggleSelectOrder = (id: string) =>
    setSelectedOrders(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const panelBorder = `1px solid ${connected ? accentColor + "55" : "#1e1e2e"}`;

  const header = (
    <div style={{
      padding: "11px 14px", borderBottom: "1px solid #1a1a2e", background: "#0d0d1a",
      borderRadius: maximized ? 0 : "10px 10px 0 0",
      display: "flex", alignItems: "center", justifyContent: "space-between",
      flexShrink: 0,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{
          width: 8, height: 8, borderRadius: "50%",
          background: connected ? accentColor : "#e74c3c",
          boxShadow: connected ? `0 0 6px ${accentColor}` : "none",
          flexShrink: 0,
        }} />
        <span style={{ fontSize: 13, fontWeight: 700, color: "#e8e8f0" }}>{title}</span>
        {conn?.user_id && <span style={{ fontSize: 11, color: "#555" }}>({conn.user_id})</span>}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {broker && connected && openPos.length > 0 && (
          <>
            <button onClick={executeAllAsMarket} disabled={executingAll} title="Cancel conflicting orders then MARKET-close all open positions" style={{
              background: "#0f1f0a", border: "1px solid #26a65b55", borderRadius: 5,
              color: "#26a65b", padding: "4px 10px", fontSize: 11, fontWeight: 600,
              cursor: executingAll ? "not-allowed" : "pointer", opacity: executingAll ? 0.5 : 1,
            }}>{executingAll ? "Executing…" : "⚡ Exec All"}</button>
            <button onClick={cancelAllOrders} disabled={cancellingAll} title="Cancel all open orders" style={{
              background: "#2a0a0a", border: "1px solid #e74c3c55", borderRadius: 5,
              color: "#e74c3c", padding: "4px 10px", fontSize: 11, fontWeight: 600,
              cursor: cancellingAll ? "not-allowed" : "pointer", opacity: cancellingAll ? 0.5 : 1,
            }}>{cancellingAll ? "Cancelling…" : "✕ All"}</button>
          </>
        )}
        {broker && connected && (
          <button onClick={() => setDrawer(d => d?.type === "new-order" ? null : { type: "new-order" })} style={{
            background: drawer?.type === "new-order" ? `${accentColor}22` : "transparent",
            border: `1px solid ${accentColor}55`, borderRadius: 5,
            color: accentColor, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer",
          }}>{drawer?.type === "new-order" ? "✕ Order" : "+ Order"}</button>
        )}
        {!connected ? (
          <a href={loginUrl} style={{
            background: "#0f1f2e", border: `1px solid ${accentColor}55`,
            borderRadius: 5, color: accentColor, padding: "4px 12px",
            fontSize: 12, textDecoration: "none", fontWeight: 600,
          }}>Connect →</a>
        ) : (
          <button onClick={onRefresh} style={{
            background: "transparent", border: "none", color: "#444", fontSize: 11, cursor: "pointer",
          }}>↺</button>
        )}
        {/* Maximize / restore */}
        <button
          onClick={() => setMaximized(m => !m)}
          title={maximized ? "Restore" : "Maximize"}
          style={{
            background: "transparent", border: `1px solid #2a2a3e`, borderRadius: 5,
            color: "#555", padding: "3px 7px", fontSize: 13, cursor: "pointer", lineHeight: 1,
          }}
        >
          {maximized ? "⊟" : "⛶"}
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* ── Close All confirmation modal ── */}
      {closeAllPlan && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.78)", zIndex: 2000,
          display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <div style={{
            background: "#10101a", border: "1px solid #2a2a3e", borderRadius: 10,
            padding: "20px 24px", minWidth: 420, maxWidth: 600,
            maxHeight: "80vh", display: "flex", flexDirection: "column", gap: 12,
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#e74c3c" }}>
              ⬛ Close All Positions ({closeAllPlan.length})
            </div>
            <div style={{ fontSize: 11, color: "#555" }}>
              Review the planned actions — each position will be closed via MARKET order.
            </div>
            <div style={{ overflow: "auto", flex: 1, marginBottom: 4 }}>
              <table style={{ ...tableStyle, marginBottom: 0 }}>
                <thead><tr>
                  {["Symbol", "Qty", "Action"].map(h => <th key={h} style={thStyle}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {closeAllPlan.map((a, i) => (
                    <tr key={i} style={i % 2 === 0 ? rowAlt : {}}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0" }}>{a.position.tradingsymbol}</td>
                      <td style={{ ...tdStyle, color: a.position.quantity > 0 ? "#26a65b" : "#e74c3c", fontWeight: 600 }}>
                        {a.position.quantity > 0 ? "+" : ""}{a.position.quantity}
                      </td>
                      <td style={{ ...tdStyle, fontSize: 11, lineHeight: 1.5 }}>
                        {a.toConvert ? (
                          <>
                            <span style={{ color: "#f39c12" }}>Convert</span>
                            {" "}…{a.toConvert.order_id.slice(-6)}
                            {a.toConvert.order_type !== "MARKET" && (
                              <span style={{ color: "#555" }}> ({a.toConvert.order_type} ₹{fmt(a.toConvert.price)})</span>
                            )}
                            {" → "}<span style={{ color: "#26a65b" }}>MARKET</span>
                            {a.toConvert.quantity !== a.closeQty && (
                              <span style={{ color: "#f39c12" }}> qty {a.toConvert.quantity}→{a.closeQty}</span>
                            )}
                            {a.toCancel.length > 0 && (
                              <span style={{ color: "#e74c3c" }}> · cancel {a.toCancel.length} extra</span>
                            )}
                          </>
                        ) : (
                          <>
                            <span style={{ color: "#26a65b" }}>New MARKET</span>
                            {" "}<span style={{ color: a.closeDir === "SELL" ? "#e74c3c" : "#26a65b", fontWeight: 700 }}>{a.closeDir}</span>
                            {" qty "}{a.closeQty}
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", paddingTop: 8, borderTop: "1px solid #1a1a2e" }}>
              <button onClick={() => setCloseAllPlan(null)} disabled={closingAll} style={{
                background: "transparent", border: "1px solid #333", borderRadius: 6,
                color: "#888", padding: "6px 18px", fontSize: 12, cursor: "pointer",
              }}>Cancel</button>
              <button onClick={executeCloseAll} disabled={closingAll} style={{
                background: "#2a0a0a", border: "1px solid #e74c3c", borderRadius: 6,
                color: "#e74c3c", padding: "6px 20px", fontSize: 12, fontWeight: 700,
                cursor: closingAll ? "not-allowed" : "pointer", opacity: closingAll ? 0.5 : 1,
              }}>{closingAll ? "Closing…" : `Close All (${closeAllPlan.length})`}</button>
            </div>
          </div>
        </div>
      )}

      {/* When maximized, keep a phantom card in the flex row so the other cards don't reflow */}
      {maximized && (
        <div style={{
          flex: 1, minWidth: 0, minHeight: 560,
          border: "1px dashed #1e1e2e", borderRadius: 10, background: "#0a0a12",
        }} />
      )}

      <div style={maximized
        ? {
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000,
            display: "flex", flexDirection: "column",
            background: "#10101a", border: panelBorder,
          }
        : {
            flex: 1, minWidth: 0, minHeight: 560, display: "flex", flexDirection: "column",
            background: "#10101a", border: panelBorder, borderRadius: 10,
          }
      }>
      {header}

      {/* Stat cards */}
      <div style={{ display: "flex", gap: 8, padding: "10px 12px", flexWrap: "wrap" }}>
        <StatCard
          label="Net P&L"
          value={`${totalPnl >= 0 ? "+" : ""}₹${fmt(totalPnl)}`}
          color={pnlColor(totalPnl)}
          sub="unrealised+realised"
          pulse={pnlPulse}
        />
        <StatCard
          label="Open Pos"
          value={String(openPos.length)}
          sub={`${closedPos.length} closed`}
          color={accentColor}
        />
        <StatCard
          label="Open Orders"
          value={String(activeOrders.length)}
          sub={`${executedOrders.length} filled`}
          color={activeOrders.length > 0 ? "#f39c12" : "#888"}
        />
        <StatCard label="Orders Today" value={String(orders.length)} color="#888" />
      </div>

      {/* Tabs + search */}
      <div style={{ display: "flex", borderBottom: "1px solid #1a1a2e", padding: "0 12px", alignItems: "center" }}>
        {(["positions", "orders", ...(broker ? ["guards"] : [])] as PanelTab[]).map(t => (
          <button key={t} onClick={() => setTab(t)} style={{
            background: "none", border: "none", cursor: "pointer",
            padding: "8px 14px", fontSize: 12, fontWeight: 600,
            color: tab === t ? accentColor : "#555",
            borderBottom: tab === t ? `2px solid ${accentColor}` : "2px solid transparent",
          }}>
            {t === "positions"
              ? `Positions (${openPos.length}${closedPos.length > 0 ? `+${closedPos.length}` : ""})`
              : t === "orders"
              ? `Orders (${filteredOrders.length})`
              : `Guards (${guards.filter(g => g.status === "WATCHING" || g.status === "ARMED").length})`}
          </button>
        ))}
        <input
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="🔍 symbol"
          style={{
            marginLeft: "auto", background: "#0d0d1a", border: "1px solid #2a2a3e",
            borderRadius: 5, color: "#e8e8f0", padding: "3px 8px", fontSize: 11,
            width: 110, outline: "none",
          }}
        />
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: "auto" }}>
        {tab === "positions" && (
          <>
            {broker && openPos.length > 0 && (
              <div style={{ padding: "6px 12px", borderBottom: "1px solid #1a1a2e", display: "flex", justifyContent: "flex-end" }}>
                <button onClick={buildCloseAllPlan} style={{
                  background: "#2a0a0a", border: "1px solid #e74c3c66", borderRadius: 5,
                  color: "#e74c3c", padding: "4px 14px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                }}>⬛ Close All ({openPos.length})</button>
              </div>
            )}
            {openPos.length === 0 && closedPos.length === 0 ? (
              <div style={emptyStyle}>No positions today</div>
            ) : (
              <table style={tableStyle}>
                <thead><tr>
                  {["Symbol", "Qty", "Avg", "LTP", "P&L", "P&L%", ...(broker ? ["Action"] : [])].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {/* Open positions */}
                  {openPos.length === 0 && (
                    <tr><td colSpan={broker ? 8 : 7} style={{ ...tdStyle, color: "#333", textAlign: "center", fontStyle: "italic" }}>No open positions</td></tr>
                  )}
                  {openPos.map((p, i) => {
                    const pct = p.average_price > 0
                      ? ((p.last_price - p.average_price) / p.average_price) * 100 : 0;
                    const key = `${p.tradingsymbol}:${p.product}`;
                    // Active orders for this symbol
                    const symOrders = activeOrders.filter(o => o.tradingsymbol === p.tradingsymbol);
                    // Active guards for this symbol (dest brokers only)
                    const symGuards = broker
                      ? guards.filter(g => g.tradingsymbol === p.tradingsymbol && (g.status === "WATCHING" || g.status === "ARMED"))
                      : [];
                    const hasPending = symOrders.length > 0 || symGuards.length > 0;
                    const colSpan = broker ? 7 : 6;
                    return (
                      <>
                        <tr key={`open-${i}`} style={i % 2 === 0 ? rowAlt : {}}>
                          <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0" }}>{p.tradingsymbol}</td>
                          <td style={{ ...tdStyle, color: p.quantity > 0 ? "#26a65b" : "#e74c3c", fontWeight: 600 }}>
                            {p.quantity > 0 ? "+" : ""}{p.quantity}
                          </td>
                          <td style={tdStyle}>{fmt(p.average_price)}</td>
                          <td style={{ ...tdStyle, color: "#4fc3f7" }}>{fmt(p.last_price)}</td>
                          <td style={{ ...tdStyle, color: pnlColor(p.pnl), fontWeight: 600 }}>
                            {p.pnl >= 0 ? "+" : ""}₹{fmt(p.pnl)}
                            <div style={{ fontSize: 9, color: "#555", marginTop: 2, fontWeight: 400, lineHeight: 1.4 }}>
                              U {p.unrealised >= 0 ? "+" : ""}₹{fmt(p.unrealised)} / R {p.realised >= 0 ? "+" : ""}₹{fmt(p.realised)}
                            </div>
                          </td>
                          <td style={{ ...tdStyle, color: pnlColor(pct) }}>{pct >= 0 ? "+" : ""}{pct.toFixed(2)}%</td>
                          {broker && (
                            <td style={{ ...tdStyle }}>
                              <span style={{ display: "inline-flex", gap: 4 }}>
                                <button
                                  onClick={() => { setCloseQty(String(Math.abs(p.quantity))); setDrawer({ type: "mkt-close", position: p }); }}
                                  disabled={closingKey === key}
                                  style={{
                                    background: "#2a0d0d", border: "1px solid #e74c3c44", borderRadius: 4,
                                    color: "#e74c3c", padding: "3px 8px", fontSize: 11, fontWeight: 600,
                                    cursor: closingKey === key ? "default" : "pointer", opacity: closingKey === key ? 0.5 : 1,
                                  }}>{closingKey === key ? "…" : "MKT"}</button>
                                <button onClick={() => openLimitSell(p)} style={{
                                  background: "#0a1020", border: "1px solid #4fc3f744", borderRadius: 4,
                                  color: "#4fc3f7", padding: "3px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                                }}>Limit</button>
                              </span>
                            </td>
                          )}
                        </tr>
                        {hasPending && (
                          <tr key={`open-pending-${i}`} style={{ background: i % 2 === 0 ? "#090910" : "#07070e" }}>
                            <td colSpan={colSpan} style={{ padding: "3px 10px 5px 24px", borderBottom: "1px solid #111" }}>
                              <div style={{ display: "flex", flexWrap: "wrap", gap: 5 }}>
                                {symOrders.map(o => (
                                  <span key={o.order_id} style={{
                                    display: "inline-flex", alignItems: "center", gap: 4,
                                    fontSize: 10, borderRadius: 4, padding: "2px 7px",
                                    background: o.transaction_type === "BUY" ? "#0a1f0a" : "#1f0a0a",
                                    border: `1px solid ${o.transaction_type === "BUY" ? "#26a65b55" : "#e74c3c55"}`,
                                    color: o.transaction_type === "BUY" ? "#26a65b" : "#e74c3c",
                                  }}>
                                    <span style={{ fontWeight: 700 }}>{o.transaction_type}</span>
                                    <span style={{ color: "#aaa" }}>{o.quantity}</span>
                                    {o.order_type !== "MARKET" && o.price > 0 && (
                                      <span style={{ color: "#666" }}>@{fmt(o.price)}</span>
                                    )}
                                    <span style={{
                                      color: o.status === "TRIGGER_PENDING" ? "#f39c12" : "#4fc3f7",
                                      fontSize: 9,
                                    }}>{o.order_type}{o.status === "TRIGGER_PENDING" ? " SL" : ""}</span>
                                  </span>
                                ))}
                                {symGuards.map(g => (
                                  <span key={g.id} style={{
                                    display: "inline-flex", alignItems: "center", gap: 4,
                                    fontSize: 10, borderRadius: 4, padding: "2px 7px",
                                    background: g.status === "ARMED" ? "#1a0a2e" : "#0e0e1e",
                                    border: `1px solid ${g.status === "ARMED" ? "#9b59b6" : "#44448866"}`,
                                    color: g.status === "ARMED" ? "#9b59b6" : "#6666aa",
                                  }}>
                                    <span>{g.status === "ARMED" ? "🔔" : "🛡"}</span>
                                    <span style={{ fontWeight: 700 }}>@{g.trigger_price}</span>
                                    <span style={{ color: "#555" }}>{g.direction}</span>
                                    {g.trail_offset > 0 && (
                                      <span style={{ color: "#888" }}>trail {g.trail_offset}</span>
                                    )}
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })}

                  {/* Closed positions section */}
                  {closedPos.length > 0 && (
                    <tr style={{ background: "#09090f" }}>
                      <td colSpan={broker ? 8 : 7} style={{
                        padding: "5px 12px", fontSize: 10, color: "#444",
                        textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #1a1a2e",
                        borderTop: "2px solid #1a1a2e",
                      }}>
                        Closed today ({closedPos.length})
                      </td>
                    </tr>
                  )}
                  {closedPos.map((p, i) => {
                    const pct = p.average_price > 0
                      ? ((p.last_price - p.average_price) / p.average_price) * 100 : 0;
                    return (
                      <tr key={`closed-${i}`} style={{ opacity: 0.45 }}>
                        <td style={{ ...tdStyle, fontWeight: 600, color: "#888" }}>
                          {p.tradingsymbol}
                          <span style={{ marginLeft: 5, fontSize: 9, color: "#444", border: "1px solid #2a2a2a", borderRadius: 3, padding: "1px 4px" }}>CLOSED</span>
                        </td>
                        <td style={{ ...tdStyle, color: "#555" }}>0</td>
                        <td style={tdStyle}>{fmt(p.average_price)}</td>
                        <td style={{ ...tdStyle, color: "#4fc3f7" }}>{fmt(p.last_price)}</td>
                        <td style={{ ...tdStyle, color: pnlColor(p.pnl), fontWeight: 600 }}>
                          {p.pnl >= 0 ? "+" : ""}₹{fmt(p.pnl)}
                        </td>
                        <td style={{ ...tdStyle, color: pnlColor(pct) }}>{pct >= 0 ? "+" : ""}{pct.toFixed(2)}%</td>
                        {broker && <td style={tdStyle} />}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        )}

        {tab === "orders" && (
          orders.length === 0 ? (
            <div style={emptyStyle}>No orders today</div>
          ) : (
            <table style={tableStyle}>
              <thead><tr>
                {["Time", "Symbol", "Type", "Qty", "Avg Fill", "Price", "Status", ...(broker ? [""] : [])].map(h => (
                  <th key={h} style={thStyle}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {/* Active orders */}
                {activeOrders.length > 0 && (<>
                  <tr style={{ background: "#0e0e1a" }}>
                    <td colSpan={broker ? 8 : 7} style={{ padding: "5px 12px", fontSize: 10, color: "#f39c12", textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #1a1a2e" }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {broker && (
                          <input type="checkbox"
                            checked={activeOrders.length > 0 && activeOrders.every(o => selectedOrders.has(o.order_id))}
                            onChange={e => setSelectedOrders(e.target.checked ? new Set(activeOrders.map(o => o.order_id)) : new Set())}
                            style={{ cursor: "pointer", accentColor: "#f39c12" }}
                          />
                        )}
                        Active ({activeOrders.length})
                        {selectedOrders.size > 0 && (
                          <span style={{ color: "#4fc3f7", fontWeight: 700 }}>{selectedOrders.size} selected</span>
                        )}
                      </span>
                    </td>
                  </tr>
                  {/* Bulk action toolbar — visible only when orders are selected */}
                  {broker && selectedOrders.size > 0 && (
                    <tr style={{ background: "#0a0f1a" }}>
                      <td colSpan={broker ? 8 : 7} style={{ padding: "6px 14px", borderBottom: "1px solid #1a1a2e" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11, color: "#555" }}>Bulk ({selectedOrders.size}):</span>
                          <button onClick={() => bulkModifySelected("market")} disabled={applyingBulk} style={{
                            background: "#0f1f0a", border: "1px solid #26a65b77", borderRadius: 5,
                            color: "#26a65b", padding: "4px 12px", fontSize: 11, fontWeight: 700,
                            cursor: applyingBulk ? "not-allowed" : "pointer", opacity: applyingBulk ? 0.5 : 1,
                          }}>⚡ → MARKET</button>
                          <span style={{ color: "#333", fontSize: 11 }}>or set price:</span>
                          <input
                            type="number" placeholder="Limit price" value={bulkPrice}
                            onChange={e => setBulkPrice(e.target.value)}
                            style={{ width: 90, background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5, color: "#e8e8f0", padding: "4px 7px", fontSize: 11 }}
                          />
                          <button onClick={() => bulkModifySelected("price")} disabled={applyingBulk || !bulkPrice} style={{
                            background: "#0a1020", border: "1px solid #4fc3f777", borderRadius: 5,
                            color: "#4fc3f7", padding: "4px 12px", fontSize: 11, fontWeight: 700,
                            cursor: (applyingBulk || !bulkPrice) ? "not-allowed" : "pointer", opacity: (applyingBulk || !bulkPrice) ? 0.5 : 1,
                          }}>Apply Price</button>
                          <button onClick={() => setSelectedOrders(new Set())} style={{
                            background: "transparent", border: "1px solid #333", borderRadius: 5,
                            color: "#555", padding: "4px 10px", fontSize: 11, cursor: "pointer",
                          }}>Deselect</button>
                        </div>
                      </td>
                    </tr>
                  )}
                </>)}
                {activeOrders.map((o, i) => (
                  <tr key={`active-${i}`} style={{ background: "#0f0e18" }}>
                    <td style={{ ...tdStyle, color: "#555", fontSize: 11 }}>{fmtTime(o.order_timestamp)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0" }}>{o.tradingsymbol}</td>
                    <td style={tdStyle}><span style={{ color: o.transaction_type === "BUY" ? "#26a65b" : "#e74c3c", fontWeight: 700 }}>{o.transaction_type}</span></td>
                    <td style={tdStyle}>{o.filled_quantity}/{o.quantity}</td>
                    <td style={{ ...tdStyle, color: "#555" }}>—</td>
                    <td style={tdStyle}>{o.price > 0 ? fmt(o.price) : "MKT"}</td>
                    <td style={tdStyle}><Badge color={ORDER_STATUS_COLOR[o.status] || "#555"} text={o.status} /></td>
                    {broker && (
                      <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                        <input type="checkbox"
                          checked={selectedOrders.has(o.order_id)}
                          onChange={() => toggleSelectOrder(o.order_id)}
                          style={{ cursor: "pointer", accentColor: "#f39c12", marginRight: 6 }}
                        />
                        <button onClick={() => startEdit(o)} style={{
                          background: drawer?.type === "modify-order" && drawer.order.order_id === o.order_id
                            ? "#0d2a1a" : "#0d1a2a",
                          border: `1px solid ${drawer?.type === "modify-order" && drawer.order.order_id === o.order_id
                            ? "#26a65b" : "#4fc3f744"}`,
                          borderRadius: 4,
                          color: drawer?.type === "modify-order" && drawer.order.order_id === o.order_id
                            ? "#26a65b" : "#4fc3f7",
                          padding: "3px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer", marginRight: 4,
                        }}>✎</button>
                        {confirmCancelId === o.order_id ? (
                          <span style={{ display: "inline-flex", gap: 4, alignItems: "center" }}>
                            <span style={{ fontSize: 10, color: "#e74c3c" }}>Cancel?</span>
                            <button onClick={() => { setConfirmCancelId(null); cancelOrder(o.order_id, o.tradingsymbol); }} style={{
                              background: "#3a0d0d", border: "1px solid #e74c3c", borderRadius: 4,
                              color: "#e74c3c", padding: "2px 7px", fontSize: 11, fontWeight: 700, cursor: "pointer",
                            }}>Yes</button>
                            <button onClick={() => setConfirmCancelId(null)} style={{
                              background: "#1a1a2e", border: "1px solid #333", borderRadius: 4,
                              color: "#888", padding: "2px 7px", fontSize: 11, cursor: "pointer",
                            }}>No</button>
                          </span>
                        ) : (
                          <button onClick={() => setConfirmCancelId(o.order_id)} style={{
                            background: "#2a0d0d", border: "1px solid #e74c3c55", borderRadius: 4,
                            color: "#e74c3c", padding: "3px 8px", fontSize: 11, fontWeight: 600, cursor: "pointer",
                          }}>✕</button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}

                {/* Executed orders */}
                {executedOrders.length > 0 && (
                  <tr style={{ background: "#09090f" }}>
                    <td colSpan={broker ? 8 : 7} style={{ padding: "5px 12px", fontSize: 10, color: "#26a65b", textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #1a1a2e", borderTop: "2px solid #1a1a2e" }}>
                      Executed ({executedOrders.length})
                    </td>
                  </tr>
                )}
                {[...executedOrders].reverse().map((o, i) => (
                  <tr key={`exec-${i}`} style={i % 2 === 0 ? rowAlt : {}}>
                    <td style={{ ...tdStyle, color: "#555", fontSize: 11 }}>{fmtTime(o.order_timestamp)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0" }}>{o.tradingsymbol}</td>
                    <td style={tdStyle}><span style={{ color: o.transaction_type === "BUY" ? "#26a65b" : "#e74c3c", fontWeight: 700 }}>{o.transaction_type}</span></td>
                    <td style={tdStyle}>{o.filled_quantity}/{o.quantity}</td>
                    <td style={{ ...tdStyle, color: "#4fc3f7", fontWeight: 600 }}>
                      {o.average_price > 0 ? `₹${fmt(o.average_price)}` : "—"}
                      {o.average_price > 0 && o.price > 0 && (
                        <span style={{ fontSize: 9, color: "#555", marginLeft: 4 }}>
                          ({o.average_price > o.price ? "+" : ""}{fmt(o.average_price - o.price)} slip)
                        </span>
                      )}
                    </td>
                    <td style={{ ...tdStyle, color: "#555" }}>{o.price > 0 ? fmt(o.price) : "MKT"}</td>
                    <td style={tdStyle}><Badge color={ORDER_STATUS_COLOR[o.status] || "#555"} text={o.status} /></td>
                    {broker && <td style={tdStyle} />}
                  </tr>
                ))}

                {/* Cancelled / Rejected */}
                {closedOrders.length > 0 && (
                  <tr style={{ background: "#09090f" }}>
                    <td colSpan={broker ? 8 : 7} style={{ padding: "5px 12px", fontSize: 10, color: "#555", textTransform: "uppercase", letterSpacing: 1, borderBottom: "1px solid #1a1a2e", borderTop: "2px solid #1a1a2e" }}>
                      Cancelled / Rejected ({closedOrders.length})
                    </td>
                  </tr>
                )}
                {[...closedOrders].reverse().map((o, i) => (
                  <tr key={`closed-${i}`} style={{ opacity: 0.5 }}>
                    <td style={{ ...tdStyle, color: "#555", fontSize: 11 }}>{fmtTime(o.order_timestamp)}</td>
                    <td style={{ ...tdStyle, fontWeight: 600, color: "#888" }}>{o.tradingsymbol}</td>
                    <td style={tdStyle}><span style={{ color: o.transaction_type === "BUY" ? "#26a65b" : "#e74c3c", fontWeight: 700 }}>{o.transaction_type}</span></td>
                    <td style={tdStyle}>{o.filled_quantity}/{o.quantity}</td>
                    <td style={{ ...tdStyle, color: "#555" }}>—</td>
                    <td style={tdStyle}>{o.price > 0 ? fmt(o.price) : "MKT"}</td>
                    <td style={tdStyle}><Badge color={ORDER_STATUS_COLOR[o.status] || "#555"} text={o.status} /></td>
                    {broker && <td style={tdStyle} />}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}

        {/* ── Guards tab ── */}
        {tab === "guards" && broker && (
          <div style={{ padding: "12px 14px" }}>
            {/* Create guard button */}
            {activeOrders.filter(o => o.order_type !== "MARKET").length > 0 && (
              <div style={{ marginBottom: 12, display: "flex", justifyContent: "flex-end" }}>
                <button
                  onClick={() => { setGuardOrders(new Set()); setDrawer({ type: "create-guard" }); }}
                  style={{
                    background: drawer?.type === "create-guard" ? `${accentColor}22` : "#0b0b14",
                    border: `1px solid ${accentColor}55`, borderRadius: 6,
                    color: accentColor, padding: "6px 16px", fontSize: 12, fontWeight: 700, cursor: "pointer",
                  }}
                >🛡 Create Guard</button>
              </div>
            )}

            {/* Guard status table */}
            {guards.length === 0 ? (
              <div style={emptyStyle}>No guards today</div>
            ) : (
              <table style={tableStyle}>
                <thead><tr>
                  {["Symbol", "Dir", "Trigger", "LTP", "Buffer/Trail", "Status", "Armed", ""].map(h => (
                    <th key={h} style={thStyle}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {guards.map(g => {
                    const GUARD_COLOR: Record<string, string> = {
                      WATCHING: "#4fc3f7", ARMED: "#f39c12", TRIGGERED: "#26a65b",
                      CANCELLED: "#555", EXPIRED: "#333",
                    };
                    const color = GUARD_COLOR[g.status] || "#555";
                    const isActive = g.status === "WATCHING" || g.status === "ARMED";
                    const armedPct = (g.status === "ARMED" && g.time_buffer_sec > 0 && g.armed_secs !== null)
                      ? Math.min(100, Math.round((g.armed_secs / g.time_buffer_sec) * 100)) : null;
                    return (
                      <tr key={g.id} style={{ opacity: isActive ? 1 : 0.45 }}>
                        <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0" }}>{g.tradingsymbol}</td>
                        <td style={{ ...tdStyle, fontSize: 10, color: g.direction === "BELOW" ? "#e74c3c" : "#26a65b" }}>{g.direction}</td>
                        <td style={{ ...tdStyle, color: "#f39c12", fontWeight: 600 }}>
                          ₹{fmt(g.trigger_price)}
                          {g.trail_offset > 0 && (
                            <span style={{ fontSize: 9, color: "#9b59b6", marginLeft: 4 }}>±{fmt(g.trail_offset)}</span>
                          )}
                        </td>
                        <td style={{ ...tdStyle, color: g.last_ltp !== null && isActive
                          ? (g.direction === "BELOW" ? (g.last_ltp < g.trigger_price ? "#e74c3c" : "#26a65b")
                             : (g.last_ltp > g.trigger_price ? "#e74c3c" : "#26a65b")) : "#555" }}>
                          {g.last_ltp !== null ? `₹${fmt(g.last_ltp)}` : "—"}
                        </td>
                        <td style={{ ...tdStyle, color: "#555", fontSize: 11 }}>
                          {g.time_buffer_sec === 0 ? "instant" : `${g.time_buffer_sec}s`}
                          {g.trail_offset > 0 && (
                            <span style={{ color: "#9b59b6", marginLeft: 4 }}>· trail</span>
                          )}
                        </td>
                        <td style={tdStyle}>
                          <Badge color={color} text={g.status} />
                        </td>
                        <td style={{ ...tdStyle, fontSize: 11 }}>
                          {g.status === "ARMED" && g.armed_secs !== null ? (
                            <span style={{ color: "#f39c12" }}>
                              {g.armed_secs.toFixed(0)}s
                              {armedPct !== null && ` (${armedPct}%)`}
                            </span>
                          ) : g.status === "TRIGGERED" && g.triggered_at ? (
                            <span style={{ color: "#26a65b" }}>{fmtTime(g.triggered_at)}</span>
                          ) : "—"}
                        </td>
                        <td style={tdStyle}>
                          {isActive && (
                            <button onClick={() => cancelGuard(g.id)} style={{
                              background: "transparent", border: "1px solid #333", borderRadius: 4,
                              color: "#666", padding: "2px 7px", fontSize: 11, cursor: "pointer",
                            }}>✕</button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>

    {/* ── Action Drawer (bottom-left corner) ── */}
    {drawer && broker && (
      <div style={{
        position: "fixed", bottom: 20, left: 20, zIndex: 1500, width: 360,
        background: "#10101a", border: `1px solid ${accentColor}55`,
        borderRadius: 10, boxShadow: "0 8px 40px rgba(0,0,0,0.75)",
        animation: "drawerIn 0.18s ease", display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>
        {/* Drawer header */}
        <div style={{
          padding: "10px 14px", background: "#0d0d1a", borderBottom: "1px solid #1a1a2e",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#e8e8f0" }}>
              {drawer.type === "mkt-close"    && "Close Position"}
              {drawer.type === "limit-order"  && `Limit ${"position" in drawer && drawer.position.quantity > 0 ? "SELL" : "BUY"}`}
              {drawer.type === "modify-order" && "Modify Order"}
              {drawer.type === "new-order"    && "New Order"}
              {drawer.type === "create-guard" && "Create Price Guard"}
            </div>
            {"position" in drawer && (
              <div style={{ fontSize: 11, color: "#555", marginTop: 1 }}>
                {drawer.position.tradingsymbol}
                <span style={{ marginLeft: 6, color: drawer.position.quantity > 0 ? "#26a65b" : "#e74c3c" }}>
                  {drawer.position.quantity > 0 ? "+" : ""}{drawer.position.quantity} qty
                </span>
              </div>
            )}
            {"order" in drawer && (
              <div style={{ fontSize: 11, color: "#555", marginTop: 1 }}>
                {drawer.order.tradingsymbol}
                <span style={{ marginLeft: 6, color: "#4fc3f7" }}>{drawer.order.order_type} @ {drawer.order.price > 0 ? `₹${fmt(drawer.order.price)}` : "MKT"}</span>
              </div>
            )}
          </div>
          <button onClick={() => setDrawer(null)} style={{
            background: "none", border: "none", color: "#555", cursor: "pointer",
            fontSize: 18, padding: "0 2px", lineHeight: 1,
          }}>✕</button>
        </div>

        {/* Drawer body */}
        <div style={{ padding: "14px 16px", display: "flex", flexDirection: "column", gap: 10 }}>

          {/* ── MKT Close ── */}
          {drawer.type === "mkt-close" && (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 12, color: "#888", minWidth: 60 }}>Qty to close</span>
                <input
                  type="number" min={1} max={Math.abs(drawer.position.quantity)}
                  value={closeQty}
                  onChange={e => setCloseQty(e.target.value)}
                  style={{ flex: 1, background: "#1a1a2e", border: "1px solid #e74c3c44", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                />
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => closePosition(drawer.position)} disabled={!!closingKey} style={{
                  flex: 1, background: "#2a0a0a", border: "1px solid #e74c3c", borderRadius: 6,
                  color: "#e74c3c", padding: "8px", fontSize: 13, fontWeight: 700,
                  cursor: closingKey ? "not-allowed" : "pointer", opacity: closingKey ? 0.5 : 1,
                }}>{closingKey ? "Sending…" : "⬛ Market Close"}</button>
                <button onClick={() => setDrawer(null)} style={{
                  background: "transparent", border: "1px solid #333", borderRadius: 6,
                  color: "#555", padding: "8px 14px", fontSize: 13, cursor: "pointer",
                }}>Cancel</button>
              </div>
            </>
          )}

          {/* ── Limit SELL / BUY ── */}
          {drawer.type === "limit-order" && (
            <>
              <div style={{ display: "flex", gap: 8 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Qty</div>
                  <input type="number" min={1} max={Math.abs(drawer.position.quantity)}
                    value={limitSellQty} onChange={e => setLimitSellQty(e.target.value)}
                    style={{ width: "100%", background: "#1a1a2e", border: "1px solid #4fc3f744", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Price ₹</div>
                  <input type="number" min={0.01} step={0.05}
                    value={limitSellPrice} onChange={e => setLimitSellPrice(e.target.value)}
                    style={{ width: "100%", background: "#1a1a2e", border: "1px solid #4fc3f744", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                  />
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={submitLimitSell} disabled={submittingLimit} style={{
                  flex: 1, background: "#0a1020", border: "1px solid #4fc3f7", borderRadius: 6,
                  color: "#4fc3f7", padding: "8px", fontSize: 13, fontWeight: 700,
                  cursor: submittingLimit ? "not-allowed" : "pointer", opacity: submittingLimit ? 0.5 : 1,
                }}>{submittingLimit ? "Placing…" : `Place LIMIT ${drawer.position.quantity > 0 ? "SELL" : "BUY"}`}</button>
                <button onClick={() => setDrawer(null)} style={{
                  background: "transparent", border: "1px solid #333", borderRadius: 6,
                  color: "#555", padding: "8px 14px", fontSize: 13, cursor: "pointer",
                }}>Cancel</button>
              </div>
            </>
          )}

          {/* ── Modify Order ── */}
          {drawer.type === "modify-order" && (
            <>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Type</div>
                  <select value={editFields.orderType} onChange={e => setEditFields(f => ({ ...f, orderType: e.target.value }))}
                    style={selStyle(86)}>
                    <option value="MARKET">MARKET</option>
                    <option value="LIMIT">LIMIT</option>
                    <option value="SL">SL</option>
                    <option value="SL-M">SL-M</option>
                  </select>
                </div>
                <div style={{ flex: 1, minWidth: 70 }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Qty</div>
                  <input type="number" value={editFields.qty}
                    onChange={e => setEditFields(f => ({ ...f, qty: e.target.value }))}
                    style={{ width: "100%", background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                  />
                </div>
                {(editFields.orderType === "LIMIT" || editFields.orderType === "SL") && (
                  <div style={{ flex: 1, minWidth: 80 }}>
                    <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Price ₹</div>
                    <input type="number" value={editFields.price}
                      onChange={e => setEditFields(f => ({ ...f, price: e.target.value }))}
                      style={{ width: "100%", background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                    />
                  </div>
                )}
                {(editFields.orderType === "SL" || editFields.orderType === "SL-M") && (
                  <div style={{ flex: 1, minWidth: 80 }}>
                    <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Trigger ₹</div>
                    <input type="number" value={editFields.trigger}
                      onChange={e => setEditFields(f => ({ ...f, trigger: e.target.value }))}
                      style={{ width: "100%", background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                    />
                  </div>
                )}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={submitModify} disabled={modifying} style={{
                  flex: 1, background: "#0f2010", border: "1px solid #26a65b", borderRadius: 6,
                  color: "#26a65b", padding: "8px", fontSize: 13, fontWeight: 700,
                  cursor: modifying ? "not-allowed" : "pointer", opacity: modifying ? 0.5 : 1,
                }}>{modifying ? "Saving…" : "Save Changes"}</button>
                <button onClick={() => setDrawer(null)} style={{
                  background: "transparent", border: "1px solid #333", borderRadius: 6,
                  color: "#555", padding: "8px 14px", fontSize: 13, cursor: "pointer",
                }}>Cancel</button>
              </div>
            </>
          )}

          {/* ── New Order ── */}
          {drawer.type === "new-order" && (
            <NewOrderForm
              broker={broker}
              accentColor={accentColor}
              onMsg={onMsg}
              onSubmitted={() => { setDrawer(null); onActionDone?.(); }}
            />
          )}

          {/* ── Create Guard ── */}
          {drawer.type === "create-guard" && (
            <>
              <div style={{ fontSize: 11, color: "#555", marginBottom: 2 }}>Select LIMIT orders to protect:</div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto" }}>
                {activeOrders.filter(o => o.order_type !== "MARKET").map(o => (
                  <label key={o.order_id} style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", fontSize: 12, padding: "4px 6px", borderRadius: 5, background: guardOrders.has(o.order_id) ? "#111a11" : "transparent" }}>
                    <input type="checkbox"
                      checked={guardOrders.has(o.order_id)}
                      onChange={() => setGuardOrders(s => { const n = new Set(s); n.has(o.order_id) ? n.delete(o.order_id) : n.add(o.order_id); return n; })}
                      style={{ accentColor: accentColor }}
                    />
                    <span style={{ color: o.transaction_type === "BUY" ? "#26a65b" : "#e74c3c", fontWeight: 700 }}>{o.transaction_type}</span>
                    <span style={{ color: "#e8e8f0", fontWeight: 600 }}>{o.tradingsymbol}</span>
                    <span style={{ color: "#555", marginLeft: "auto" }}>{o.order_type} @ {o.price > 0 ? fmt(o.price) : "MKT"}</span>
                  </label>
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 70 }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Direction</div>
                  <select value={guardDirection} onChange={e => setGuardDirection(e.target.value as any)} style={{ ...selStyle(80), width: "100%" }}>
                    <option value="BELOW">BELOW</option>
                    <option value="ABOVE">ABOVE</option>
                  </select>
                </div>
                <div style={{ flex: 1.5, minWidth: 90 }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Trigger ₹</div>
                  <input type="number" placeholder="Price" value={guardTrigger}
                    onChange={e => setGuardTrigger(e.target.value)}
                    style={{ width: "100%", background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 70 }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Trail ±₹</div>
                  <input type="number" placeholder="0=fixed" min={0} step={0.05} value={guardTrail}
                    onChange={e => setGuardTrail(e.target.value)}
                    style={{ width: "100%", background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                  />
                </div>
                <div style={{ flex: 1, minWidth: 70 }}>
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 4 }}>Buffer sec</div>
                  <input type="number" placeholder="0" min={0} value={guardBuffer}
                    onChange={e => setGuardBuffer(e.target.value)}
                    style={{ width: "100%", background: "#1a1a2e", border: "1px solid #2a2a3e", borderRadius: 5, color: "#e8e8f0", padding: "6px 10px", fontSize: 13 }}
                  />
                </div>
              </div>
              <div style={{ fontSize: 11, color: "#444" }}>
                {parseFloat(guardTrail) > 0 ? `Trailing ±₹${guardTrail}` : parseInt(guardBuffer, 10) === 0 ? "→ Triggers instantly on breach" : `→ Must hold for ${guardBuffer}s`}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={createGuards} disabled={creatingGuard || guardOrders.size === 0 || !guardTrigger} style={{
                  flex: 1, background: `${accentColor}22`, border: `1px solid ${accentColor}66`, borderRadius: 6,
                  color: accentColor, padding: "8px", fontSize: 13, fontWeight: 700,
                  cursor: (creatingGuard || guardOrders.size === 0 || !guardTrigger) ? "not-allowed" : "pointer",
                  opacity: (creatingGuard || guardOrders.size === 0 || !guardTrigger) ? 0.4 : 1,
                }}>{creatingGuard ? "Creating…" : `🛡 Guard (${guardOrders.size} order${guardOrders.size !== 1 ? "s" : ""})`}</button>
                <button onClick={() => setDrawer(null)} style={{
                  background: "transparent", border: "1px solid #333", borderRadius: 6,
                  color: "#555", padding: "8px 14px", fontSize: 13, cursor: "pointer",
                }}>Cancel</button>
              </div>
            </>
          )}

        </div>
      </div>
    )}

    </>
  );
};

// ─── Broker Controls (enable toggle + % input, per destination) ──────────────

const BrokerControls = ({
  broker, config, connected, pctInput, onToggle, onPctChange, onSavePct, savingPct,
}: {
  broker: Broker;
  config: BrokerConfig | null;
  connected: boolean;
  pctInput: string;
  onToggle: () => void;
  onPctChange: (v: string) => void;
  onSavePct: () => void;
  savingPct: boolean;
}) => {
  const meta = BROKER_META[broker];
  const isOn = !!config?.is_enabled && connected;
  // Turning ON requires a live connection; turning OFF is always allowed
  // (e.g. to silence a broker that was enabled but then disconnected).
  const canClick = connected || !!config?.is_enabled;

  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      background: "#10101a", border: `1px solid ${meta.accentColor}33`, borderRadius: 8,
      padding: "8px 14px",
    }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: meta.accentColor, minWidth: 52 }}>{meta.label}</span>

      <button
        onClick={onToggle}
        disabled={!canClick}
        title={connected ? undefined : `${meta.label} not connected — connect first to enable mirroring`}
        style={{
          background: isOn ? `${meta.accentColor}22` : "#1a1a1a",
          border: `1px solid ${isOn ? meta.accentColor : "#333"}`,
          borderRadius: 20, padding: "4px 14px",
          cursor: canClick ? "pointer" : "not-allowed",
          color: isOn ? meta.accentColor : "#555", fontWeight: 700, fontSize: 12,
          opacity: canClick ? 1 : 0.5,
        }}
      >
        {!connected
          ? (config?.is_enabled ? "○ Disconnected (tap to disable)" : "○ Not connected")
          : config?.is_enabled ? "● Mirror ON" : "○ Mirror OFF"}
      </button>

      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ fontSize: 11, color: "#555" }}>Dest %</span>
        <input
          type="number" min={1} max={100} value={pctInput}
          onChange={e => onPctChange(e.target.value)}
          style={{
            width: 50, background: "#1a1a2e", border: "1px solid #2a2a3e",
            borderRadius: 5, color: "#e8e8f0", padding: "4px 6px",
            fontSize: 13, textAlign: "center",
          }}
        />
        <button onClick={onSavePct} disabled={savingPct} style={{
          background: "#1a2a1a", border: "1px solid #26a65b44",
          borderRadius: 5, color: "#26a65b", padding: "4px 10px",
          fontSize: 12, cursor: "pointer", opacity: savingPct ? 0.5 : 1,
        }}>Save</button>
      </div>
    </div>
  );
};

// ─── Mirror Log ───────────────────────────────────────────────────────────────

const MirrorLog = ({ broker, entries }: { broker: Broker; entries: MirrorEntry[] }) => {
  const meta = BROKER_META[broker];
  const mirrored = entries.filter(m => m.mirror_status === "MIRRORED").length;
  const failed   = entries.filter(m => m.mirror_status === "FAILED").length;
  const cancelled = entries.filter(m => m.mirror_status === "CANCELLED_ON_DEST").length;

  return (
    <div style={{ background: "#10101a", border: "1px solid #1e1e2e", borderRadius: 10, marginTop: 14 }}>
      <div style={{
        padding: "11px 16px", borderBottom: "1px solid #1a1a2e", background: "#0d0d1a",
        borderRadius: "10px 10px 0 0", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
      }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: meta.accentColor }}>
          🔁 {meta.label} Mirror Log ({entries.length})
        </span>
        <span style={{ fontSize: 11, color: "#26a65b" }}>✅ {mirrored}</span>
        {failed > 0 && <span style={{ fontSize: 11, color: "#e74c3c" }}>❌ {failed}</span>}
        {cancelled > 0 && <span style={{ fontSize: 11, color: "#9b59b6" }}>↩ {cancelled}</span>}
      </div>
      {entries.length === 0 ? (
        <div style={emptyStyle}>No mirror activity today</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={tableStyle}>
            <thead><tr>
              {["Time", "Symbol", "Type", "Src Qty", "Pct", "Lot", "Dest Qty", "Src Status", "Dest Status", "Mirror", "Error"].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {entries.map((m, i) => (
                <tr key={i} style={i % 2 === 0 ? rowAlt : {}}>
                  <td style={{ ...tdStyle, color: "#555", fontSize: 11 }}>{fmtTime(m.created_at || "")}</td>
                  <td style={{ ...tdStyle, fontWeight: 600, color: "#e8e8f0" }}>{m.tradingsymbol}</td>
                  <td style={tdStyle}>
                    <span style={{ color: m.transaction_type === "BUY" ? "#26a65b" : "#e74c3c", fontWeight: 700 }}>
                      {m.transaction_type || "—"}
                    </span>
                  </td>
                  <td style={tdStyle}>{m.source_qty}</td>
                  <td style={{ ...tdStyle, color: "#555" }}>{m.mirror_pct_applied > 0 ? `${m.mirror_pct_applied}%` : "—"}</td>
                  <td style={{ ...tdStyle, color: "#444" }}>{m.lot_size_used ?? "—"}</td>
                  <td style={{ ...tdStyle, fontWeight: 600 }}>{m.dest_qty ?? "—"}</td>
                  <td style={tdStyle}><Badge color={ORDER_STATUS_COLOR[m.source_status] || "#555"} text={m.source_status} /></td>
                  <td style={tdStyle}>
                    {m.dest_status
                      ? <Badge color={ORDER_STATUS_COLOR[m.dest_status] || "#555"} text={m.dest_status} />
                      : <span style={{ color: "#333" }}>—</span>
                    }
                  </td>
                  <td style={tdStyle}><Badge color={MIRROR_STATUS_COLOR[m.mirror_status] || "#555"} text={m.mirror_status} /></td>
                  <td style={{ ...tdStyle, color: "#e74c3c", fontSize: 11, maxWidth: 180 }}>
                    {m.mirror_error ? (
                      <span title={m.mirror_error} style={{ cursor: "help" }}>
                        {m.mirror_error.length > 40 ? m.mirror_error.slice(0, 40) + "…" : m.mirror_error}
                      </span>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// ─── P&L History Panel ───────────────────────────────────────────────────────

const BROKER_COLOR: Record<string, string> = {
  kite1: "#4fc3f7", kite2: "#26a65b", upstox: "#f39c12",
};

interface PnlHistoryPanelProps {
  snapshots: PnlSnapshot[];
  loading: boolean;
  onRefresh: () => void;
  onClose: () => void;
}

const PnlHistoryPanel = ({ snapshots, loading, onRefresh, onClose }: PnlHistoryPanelProps) => {
  const [filterBroker, setFilterBroker] = useState<string>("all");

  const filtered = filterBroker === "all"
    ? snapshots
    : snapshots.filter(s => s.broker === filterBroker);

  // Group by trade_date for summary rows
  const byDate = filtered.reduce<Record<string, PnlSnapshot[]>>((acc, s) => {
    (acc[s.trade_date] = acc[s.trade_date] || []).push(s);
    return acc;
  }, {});

  const dates = Object.keys(byDate).sort((a, b) => b.localeCompare(a));

  return (
    <div style={{
      marginTop: 14, background: "#0d0d1a", border: "1px solid #1e1e3e",
      borderRadius: 8, overflow: "hidden",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "10px 14px", background: "#10101e", borderBottom: "1px solid #1e1e3e",
      }}>
        <span style={{ fontWeight: 700, fontSize: 13, color: "#e8e8f0" }}>📈 P&L History</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {/* Broker filter */}
          <div style={{ display: "flex", gap: 4 }}>
            {["all", "kite1", "kite2", "upstox"].map(b => (
              <button key={b} onClick={() => setFilterBroker(b)} style={{
                background: filterBroker === b ? "#1e2a3a" : "transparent",
                border: `1px solid ${filterBroker === b ? BROKER_COLOR[b] || "#4fc3f7" : "#333"}`,
                borderRadius: 4, color: BROKER_COLOR[b] || "#888",
                padding: "3px 8px", fontSize: 11, cursor: "pointer",
              }}>{b === "all" ? "All" : b}</button>
            ))}
          </div>
          <button onClick={onRefresh} disabled={loading} style={{
            background: "transparent", border: "1px solid #333", borderRadius: 4,
            color: "#555", padding: "3px 8px", fontSize: 11, cursor: "pointer",
          }}>{loading ? "…" : "↺"}</button>
          <button onClick={onClose} style={{
            background: "transparent", border: "none", color: "#555",
            fontSize: 16, cursor: "pointer", padding: "0 4px",
          }}>×</button>
        </div>
      </div>

      {/* Table */}
      <div style={{ overflowX: "auto", maxHeight: 400, overflowY: "auto" }}>
        {loading && filtered.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#444", fontSize: 12 }}>Loading…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 30, textAlign: "center", color: "#444", fontSize: 12 }}>
            No snapshots saved yet. Click "💾 Save P&L" to capture today's P&L.
          </div>
        ) : (
          <table style={{ ...tableStyle, minWidth: 640 }}>
            <thead>
              <tr style={{ background: "#0b0b14" }}>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Broker</th>
                <th style={{ ...thStyle, textAlign: "right" }}>P&L</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Realised</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Unrealised</th>
                <th style={thStyle}>Saved By</th>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Note</th>
              </tr>
            </thead>
            <tbody>
              {dates.map(d => {
                const rows = byDate[d];
                return rows.map((s, i) => (
                  <tr key={s.id} style={i % 2 === 0 ? rowAlt : {}}>
                    {i === 0 && (
                      <td rowSpan={rows.length} style={{
                        ...tdStyle, fontWeight: 700, fontSize: 12,
                        color: "#888", verticalAlign: "top", paddingTop: 8,
                        borderRight: "1px solid #1a1a2e",
                      }}>{d}</td>
                    )}
                    <td style={{ ...tdStyle }}>
                      <span style={{
                        color: BROKER_COLOR[s.broker] || "#888",
                        fontWeight: 600, fontSize: 11,
                        textTransform: "uppercase",
                      }}>{s.broker}</span>
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", fontWeight: 700, color: pnlColor(s.pnl) }}>
                      {s.pnl >= 0 ? "+" : ""}₹{fmt(s.pnl)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", color: pnlColor(s.realised), fontSize: 11 }}>
                      {s.realised >= 0 ? "+" : ""}₹{fmt(s.realised)}
                    </td>
                    <td style={{ ...tdStyle, textAlign: "right", color: pnlColor(s.unrealised), fontSize: 11 }}>
                      {s.unrealised >= 0 ? "+" : ""}₹{fmt(s.unrealised)}
                    </td>
                    <td style={{ ...tdStyle }}>
                      <span style={{
                        fontSize: 10, color: s.saved_by === "auto" ? "#9b59b6" : "#4fc3f7",
                        background: s.saved_by === "auto" ? "#1a0a2e" : "#0a1a2e",
                        padding: "2px 6px", borderRadius: 3,
                      }}>{s.saved_by}</span>
                    </td>
                    <td style={{ ...tdStyle, color: "#555", fontSize: 11 }}>
                      {s.saved_at ? new Date(s.saved_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"}
                    </td>
                    <td style={{ ...tdStyle, color: "#666", fontSize: 11 }}>{s.note || "—"}</td>
                  </tr>
                ));
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Daily totals footer when "all" filter is active */}
      {filterBroker === "all" && dates.length > 0 && (
        <div style={{
          padding: "8px 14px", borderTop: "1px solid #1e1e3e",
          display: "flex", gap: 18, flexWrap: "wrap",
        }}>
          {dates.slice(0, 5).map(d => {
            const rows = byDate[d];
            const total = rows.reduce((s, r) => s + r.pnl, 0);
            return (
              <span key={d} style={{ fontSize: 11 }}>
                <span style={{ color: "#555" }}>{d}: </span>
                <span style={{ color: pnlColor(total), fontWeight: 700 }}>
                  {total >= 0 ? "+" : ""}₹{fmt(total)}
                </span>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Main Page ────────────────────────────────────────────────────────────────

const TradingPage = () => {
  const { setTitle } = useSiteContext();

  const [data, setData]               = useState<any>(null);
  const [config, setConfig]           = useState<MirrorConfigResponse | null>(null);
  const [src1Conn, setSrc1Conn]       = useState<AccountConn | null>(null);
  const [kite2Conn, setKite2Conn]     = useState<AccountConn | null>(null);
  const [upstoxConn, setUpstoxConn]   = useState<AccountConn | null>(null);
  const [initialLoad, setInitialLoad] = useState(true);
  const [syncing, setSyncing]         = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [pctInputs, setPctInputs]     = useState<Record<Broker, string>>({ kite2: "10", upstox: "10" });
  const [savingPct, setSavingPct]     = useState<Record<Broker, boolean>>({ kite2: false, upstox: false });
  const [toasts, setToasts]           = useState<Toast[]>([]);
  const [savingPnl, setSavingPnl]     = useState(false);
  const [pnlHistory, setPnlHistory]   = useState<PnlSnapshot[]>([]);
  const [showPnlHistory, setShowPnlHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const toastCounter                  = useRef(0);
  const timerRef  = useRef<NodeJS.Timeout | null>(null);
  const firstLoad = useRef(true);

  const addToast = useCallback((msg: string, ok = true) => {
    const id = ++toastCounter.current;
    setToasts(t => [...t, { id, msg, ok }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 3500);
  }, []);

  const dismissToast = useCallback((id: number) => setToasts(t => t.filter(x => x.id !== id)), []);

  useEffect(() => { setTitle("Trading"); }, []);

  const fetchConnections = useCallback(() => {
    get(KITE_URLS.STATUS, { showLoading: false }).subscribe({
      next: (r: any) => setSrc1Conn({ is_connected: r.data?.is_connected, user_id: r.data?.user_id }),
      error: () => setSrc1Conn({ is_connected: false }),
    });
    get(KITE2_URLS.STATUS, { showLoading: false }).subscribe({
      next: (r: any) => setKite2Conn({ is_connected: r.data?.is_connected, user_id: r.data?.user_id }),
      error: () => setKite2Conn({ is_connected: false }),
    });
    get(UPSTOX_URLS.STATUS, { showLoading: false }).subscribe({
      next: (r: any) => setUpstoxConn({ is_connected: r.data?.is_connected, user_id: r.data?.user_id }),
      error: () => setUpstoxConn({ is_connected: false }),
    });
  }, []);

  const fetchConfig = useCallback(() => {
    get(MIRROR_URLS.CONFIG, { showLoading: false }).subscribe({
      next: (r: any) => {
        setConfig(r.data);
        setPctInputs({
          kite2: String(r.data?.kite2?.mirror_pct ?? 10),
          upstox: String(r.data?.upstox?.mirror_pct ?? 10),
        });
      },
      error: () => {},
    });
  }, []);

  const fetchData = useCallback(() => {
    if (firstLoad.current) setInitialLoad(true);
    else setSyncing(true);

    get(MIRROR_URLS.TODAY, { showLoading: false }).subscribe({
      next: (r: any) => {
        setData(r.data);
        setInitialLoad(false);
        setSyncing(false);
        setLastUpdated(new Date());
        firstLoad.current = false;
      },
      error: () => { setInitialLoad(false); setSyncing(false); firstLoad.current = false; },
    });
  }, []);

  useEffect(() => {
    fetchConnections();
    fetchConfig();
    fetchData();
    timerRef.current = setInterval(fetchData, 5_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [fetchData, fetchConnections, fetchConfig]);

  // R key → manual refresh (skip if focus is inside an input)
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "r" || e.key === "R") fetchData();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [fetchData]);

  const toggleMirror = (broker: Broker) => {
    if (!config) return;
    const next = !config[broker].is_enabled;
    put(MIRROR_URLS.CONFIG, { broker, is_enabled: next }).subscribe({
      next: () => setConfig(c => c ? { ...c, [broker]: { ...c[broker], is_enabled: next } } : c),
      error: () => {},
    });
  };

  const savePct = (broker: Broker) => {
    const pct = parseFloat(pctInputs[broker]);
    if (isNaN(pct) || pct < 1 || pct > 100) return;
    setSavingPct(s => ({ ...s, [broker]: true }));
    put(MIRROR_URLS.CONFIG, { broker, mirror_pct: pct }).subscribe({
      next: () => {
        setConfig(c => c ? { ...c, [broker]: { ...c[broker], mirror_pct: pct } } : c);
        setSavingPct(s => ({ ...s, [broker]: false }));
      },
      error: () => setSavingPct(s => ({ ...s, [broker]: false })),
    });
  };

  const fetchPnlHistory = useCallback(() => {
    setLoadingHistory(true);
    get(PNL_SNAPSHOT_URLS.history, { showLoading: false }).subscribe({
      next: (r: any) => { setPnlHistory(r.data?.snapshots || []); setLoadingHistory(false); },
      error: () => setLoadingHistory(false),
    });
  }, []);

  const savePnl = () => {
    setSavingPnl(true);
    post(PNL_SNAPSHOT_URLS.save, {}, { showLoading: false, handleError: (msg: string) => { addToast(msg, false); setSavingPnl(false); } }).subscribe({
      next: () => {
        addToast("P&L saved for all accounts", true);
        setSavingPnl(false);
        if (showPnlHistory) fetchPnlHistory();
      },
    });
  };

  const togglePnlHistory = () => {
    if (!showPnlHistory) fetchPnlHistory();
    setShowPnlHistory(v => !v);
  };

  const srcOrders: Order[]    = data?.source_orders    || [];
  const srcPos:    Position[] = data?.source_positions || [];
  const poller                = data?.poller           || {};

  const kite2:  DestinationBlock = data?.destinations?.kite2  || { orders: [], positions: [], mirror_map: [], summary: { total_mirrored: 0, failed: 0, skipped: 0, cancelled_on_dest: 0, pnl: 0 } };
  const upstox: DestinationBlock = data?.destinations?.upstox || { orders: [], positions: [], mirror_map: [], summary: { total_mirrored: 0, failed: 0, skipped: 0, cancelled_on_dest: 0, pnl: 0 } };

  return (
    <div style={{ padding: "16px 20px", minHeight: "100vh", background: "#07070f", color: "#e8e8f0" }}>
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />

      {/* ── Header ── */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>💼 Trading</h2>
          <div style={{ fontSize: 11, color: "#444", marginTop: 2 }}>
            Source Kite1 → Destinations Kite2 &amp; Upstox · Mirror: NIFTY &amp; SENSEX options
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {/* Market status */}
          <span style={{ fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{
              width: 7, height: 7, borderRadius: "50%",
              background: poller.market_hours ? "#26a65b" : "#2a2a2a",
              boxShadow: poller.market_hours ? "0 0 5px #26a65b" : "none",
            }} />
            <span style={{ color: "#555" }}>{poller.market_hours ? "Market open" : "Market closed"}</span>
          </span>

          {/* Sync */}
          {lastUpdated && (
            <span style={{ fontSize: 11, color: syncing ? "#4fc3f7" : "#444" }}>
              {syncing ? "⟳" : "↺"} {syncing ? "syncing" : lastUpdated.toLocaleTimeString("en-IN")}
            </span>
          )}

          <button onClick={fetchData} disabled={syncing || initialLoad} style={{
            background: "#1e1e2e", border: "1px solid #2a2a3e", borderRadius: 6,
            color: "#4fc3f7", padding: "5px 12px", fontSize: 12, cursor: "pointer",
            opacity: syncing || initialLoad ? 0.5 : 1,
          }}>↺ Refresh</button>

          <button onClick={savePnl} disabled={savingPnl} style={{
            background: savingPnl ? "#1a1a2e" : "#0f2a1a", border: "1px solid #26a65b",
            borderRadius: 6, color: "#26a65b", padding: "5px 12px", fontSize: 12,
            cursor: savingPnl ? "not-allowed" : "pointer", opacity: savingPnl ? 0.6 : 1,
          }}>{savingPnl ? "Saving…" : "💾 Save P&L"}</button>

          <button onClick={togglePnlHistory} style={{
            background: showPnlHistory ? "#1a1a2e" : "#0b0b14",
            border: `1px solid ${showPnlHistory ? "#4fc3f7" : "#2a2a3e"}`,
            borderRadius: 6, color: showPnlHistory ? "#4fc3f7" : "#666",
            padding: "5px 12px", fontSize: 12, cursor: "pointer",
          }}>📈 History</button>
        </div>
      </div>

      {/* ── Per-destination controls (enable + %), fully independent ── */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
        <BrokerControls
          broker="kite2"
          config={config?.kite2 ?? null}
          connected={!!kite2Conn?.is_connected}
          pctInput={pctInputs.kite2}
          onToggle={() => toggleMirror("kite2")}
          onPctChange={v => setPctInputs(p => ({ ...p, kite2: v }))}
          onSavePct={() => savePct("kite2")}
          savingPct={savingPct.kite2}
        />
        <BrokerControls
          broker="upstox"
          config={config?.upstox ?? null}
          connected={!!upstoxConn?.is_connected}
          pctInput={pctInputs.upstox}
          onToggle={() => toggleMirror("upstox")}
          onPctChange={v => setPctInputs(p => ({ ...p, upstox: v }))}
          onSavePct={() => savePct("upstox")}
          savingPct={savingPct.upstox}
        />
        {config?.lot_sizes && config.lot_sizes.length > 0 && (
          <span style={{
            marginLeft: "auto", alignSelf: "center", fontSize: 10, color: "#333",
          }}>
            {config.lot_sizes.map(ls => `${ls.symbol_prefix}=${ls.lot_size}`).join(" · ")}
          </span>
        )}
      </div>

      {/* ── Summary strip ── */}
      <div style={{
        display: "flex", gap: 20, marginBottom: 14, flexWrap: "wrap", alignItems: "center",
        background: "#10101a", border: "1px solid #1a1a2e", borderRadius: 8, padding: "8px 14px",
      }}>
        <span style={{ fontSize: 11, color: "#26a65b", fontWeight: 700 }}>Kite2:</span>
        <span style={{ fontSize: 12, color: "#26a65b" }}>✅ {kite2.summary.total_mirrored}</span>
        {kite2.summary.failed > 0 && <span style={{ fontSize: 12, color: "#e74c3c" }}>❌ {kite2.summary.failed}</span>}
        <span style={{ fontSize: 12, color: "#555" }}>⊘ {kite2.summary.skipped}</span>
        <span style={{ fontSize: 12, color: "#9b59b6" }}>↩ {kite2.summary.cancelled_on_dest}</span>

        <span style={{ width: 1, height: 16, background: "#222" }} />

        <span style={{ fontSize: 11, color: "#f39c12", fontWeight: 700 }}>Upstox:</span>
        <span style={{ fontSize: 12, color: "#26a65b" }}>✅ {upstox.summary.total_mirrored}</span>
        {upstox.summary.failed > 0 && <span style={{ fontSize: 12, color: "#e74c3c" }}>❌ {upstox.summary.failed}</span>}
        <span style={{ fontSize: 12, color: "#555" }}>⊘ {upstox.summary.skipped}</span>
        <span style={{ fontSize: 12, color: "#9b59b6" }}>↩ {upstox.summary.cancelled_on_dest}</span>
      </div>

      {/* ── Initial spinner ── */}
      {initialLoad && (
        <div style={{ padding: 60, textAlign: "center" }}>
          <div style={{
            width: 34, height: 34, borderRadius: "50%",
            border: "3px solid #1e1e2e", borderTop: "3px solid #4fc3f7",
            margin: "0 auto 12px", animation: "spin 0.8s linear infinite",
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ color: "#444", fontSize: 13 }}>Loading from Kite...</div>
        </div>
      )}

      {!initialLoad && (
        <>
          {/* ── Three account panels ── */}
          <div style={{ display: "flex", gap: 12, alignItems: "stretch", flexWrap: "wrap" }}>
            <AccountPanel
              title="Source — Kite1"
              accentColor="#4fc3f7"
              conn={src1Conn}
              loginUrl={KITE_URLS.LOGIN}
              positions={srcPos}
              orders={srcOrders}
              onRefresh={fetchConnections}
              onMsg={addToast}
            />
            <AccountPanel
              title="Destination — Kite2"
              accentColor={BROKER_META.kite2.accentColor}
              conn={kite2Conn}
              loginUrl={BROKER_META.kite2.loginUrl}
              positions={kite2.positions}
              orders={kite2.orders}
              onRefresh={fetchConnections}
              broker="kite2"
              onActionDone={fetchData}
              onMsg={addToast}
            />
            <AccountPanel
              title="Destination — Upstox"
              accentColor={BROKER_META.upstox.accentColor}
              conn={upstoxConn}
              loginUrl={BROKER_META.upstox.loginUrl}
              positions={upstox.positions}
              orders={upstox.orders}
              onRefresh={fetchConnections}
              broker="upstox"
              onActionDone={fetchData}
              onMsg={addToast}
            />
          </div>

          {/* ── Two separate mirror logs ── */}
          <MirrorLog broker="kite2" entries={kite2.mirror_map} />
          <MirrorLog broker="upstox" entries={upstox.mirror_map} />

          {/* ── P&L History panel ── */}
          {showPnlHistory && (
            <PnlHistoryPanel
              snapshots={pnlHistory}
              loading={loadingHistory}
              onRefresh={fetchPnlHistory}
              onClose={() => setShowPnlHistory(false)}
            />
          )}
        </>
      )}

      <div style={{ marginTop: 10, fontSize: 10, color: "#2a2a2a", textAlign: "right" }}>
        Backend polls every 5s · frontend refreshes every 5s · market hours 9:00–15:35 IST
      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const tableStyle: React.CSSProperties = { width: "100%", borderCollapse: "collapse", fontSize: 12 };

const thStyle: React.CSSProperties = {
  padding: "9px 12px", textAlign: "left", fontSize: 10, fontWeight: 600,
  color: "#555", textTransform: "uppercase", letterSpacing: 0.5,
  borderBottom: "1px solid #1a1a2e", whiteSpace: "nowrap", background: "#0d0d1a",
};

const tdStyle: React.CSSProperties = {
  padding: "8px 12px", borderBottom: "1px solid #14141f", color: "#bbb", whiteSpace: "nowrap",
};

const rowAlt: React.CSSProperties = { background: "#0a0a14" };

const emptyStyle: React.CSSProperties = {
  padding: 24, textAlign: "center", color: "#444", fontSize: 13,
};

export default TradingPage;
